import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';

import { contactsApi } from '@/api/services/contacts.api';
import { voiceApi } from '@/api/services/voice.api';
import { handleMutationError } from '@/lib/handle-mutation-error';
import { useVoiceStore } from '../state/voice-ui.store';
import type { ITransferCallInput, IUpdateCallDispositionInput } from '../types';
import { voiceKeys } from '../query-keys';

/**
 * In-call control commands (end/hold/resume/transfer). No success toasts —
 * the `/voice` socket's `voice:call:state`/`voice:call:ended` events are the
 * real feedback loop for the widget; a toast on every hold/resume click
 * would be noisy. Failures still surface so the agent knows a click didn't
 * take effect.
 */
export function useInitiateCall() {
    return useMutation({
        mutationFn: voiceApi.initiateCall,
        onError: (error: unknown) => handleMutationError(error, 'Failed to start the call'),
    });
}

export function useEndCall() {
    return useMutation({
        mutationFn: voiceApi.endCall,
        onError: (error: unknown) => handleMutationError(error, 'Failed to end the call'),
    });
}

export function useHoldCall() {
    return useMutation({
        mutationFn: voiceApi.holdCall,
        // Optimistic: flip the button now; the socket `voice:call:state`
        // (ON_HOLD) confirms — waiting for that round trip made the button
        // look dead for a second or two.
        onMutate: (callId: string) => {
            useVoiceStore.getState().patchActiveCall(callId, { onHold: true });
        },
        onError: (error: unknown, callId) => {
            useVoiceStore.getState().patchActiveCall(callId, { onHold: false });
            handleMutationError(error, 'Failed to hold the call');
        },
    });
}

export function useResumeCall() {
    return useMutation({
        mutationFn: voiceApi.resumeCall,
        onMutate: (callId: string) => {
            useVoiceStore.getState().patchActiveCall(callId, { onHold: false });
        },
        onError: (error: unknown, callId) => {
            useVoiceStore.getState().patchActiveCall(callId, { onHold: true });
            handleMutationError(error, 'Failed to resume the call');
        },
    });
}

export function useTransferCall() {
    return useMutation({
        mutationFn: ({ id, ...data }: ITransferCallInput & { id: string }) =>
            voiceApi.transferCall(id, data),
        onError: (error: unknown) => handleMutationError(error, 'Failed to transfer the call'),
    });
}

export function useUpdateCallDisposition() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, ...data }: IUpdateCallDispositionInput & { id: string }) =>
            voiceApi.updateDisposition(id, data),
        onSuccess: (_data, { id, disposition }) => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.detail(id) });
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            // The contact-page notes view projects call notes; the mutation
            // doesn't know the contact id, so invalidate the prefix (same
            // approach as useSaveCallContact below).
            void queryClient.invalidateQueries({ queryKey: ['contacts'] });
            // Mirror the backend's own distinction: a payload without a
            // disposition is a notes-only save, not an outcome change.
            toast.success(disposition ? 'Call outcome saved' : 'Call notes updated');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to save the call outcome'),
    });
}

interface SaveCallContactInput {
    readonly callId: string;
    readonly firstName: string;
    readonly phone: string;
}

/**
 * Explicit "Save to contacts" from the post-call wrap-up card. Calls are never
 * auto-saved as contacts — this creates the org contact (reusing the standard
 * `POST /contacts`) and links it back to the just-ended call in one step.
 *
 * Duplicate race: if a contact for this number already exists (saved from
 * another card or imported meanwhile), the create 409s — we fall back to
 * finding that contact by phone and linking it, so the agent still ends up
 * with the call attached instead of an error.
 */
export function useSaveCallContact() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ callId, firstName, phone }: SaveCallContactInput) => {
            const contactId = await createOrFindContactByPhone(firstName, phone);
            await voiceApi.linkContact(callId, { contactId });
            return contactId;
        },
        onSuccess: (_contactId, { callId }) => {
            // Refresh the call (so the card/history show the linked contact) and
            // the contacts/segments lists (so the new contact appears).
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.detail(callId) });
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            void queryClient.invalidateQueries({ queryKey: ['contacts'] });
            void queryClient.invalidateQueries({ queryKey: ['segments'] });
            toast.success('Contact saved');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to save the contact'),
    });
}

interface SaveNumberAsContactInput {
    readonly firstName: string;
    readonly phone: string;
}

/**
 * Call-agnostic "Save number" from the widget's History detail view — creates
 * the org contact without linking any call (past calls stay as they were; the
 * backend links future calls by number at creation). The detail header picks
 * up the new name via its `['contacts']`-family lookup, invalidated here.
 */
export function useSaveNumberAsContact() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ firstName, phone }: SaveNumberAsContactInput) =>
            createOrFindContactByPhone(firstName, phone),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            void queryClient.invalidateQueries({ queryKey: ['contacts'] });
            void queryClient.invalidateQueries({ queryKey: ['segments'] });
            toast.success('Contact saved');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to save the contact'),
    });
}

/**
 * Delete a saved contact from the widget's History detail view ("Delete
 * contact" for a saved number). Same soft delete as the Contacts page —
 * defined here rather than importing `features/contacts` hooks (features
 * stay independent; the shared surface is the API layer).
 */
export function useDeleteSavedContact() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (contactId: string) => contactsApi.remove(contactId),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: voiceKeys.calls.all });
            void queryClient.invalidateQueries({ queryKey: ['contacts'] });
            void queryClient.invalidateQueries({ queryKey: ['segments'] });
            toast.success('Contact deleted');
        },
        onError: (error: unknown) => handleMutationError(error, 'Failed to delete the contact'),
    });
}

/**
 * Create a contact, falling back to the existing one on a duplicate 409 (the
 * number may have been saved from another card or imported meanwhile).
 * Returns the contact id either way.
 */
async function createOrFindContactByPhone(firstName: string, phone: string): Promise<string> {
    try {
        const created = await contactsApi.create({ firstName, phone });
        return created.id;
    } catch (error) {
        if (!axios.isAxiosError(error) || error.response?.status !== 409) throw error;
        const existingId = await findContactIdByPhone(phone);
        if (!existingId) throw error;
        return existingId;
    }
}

/** Resolve an existing contact's id by exact phone (duplicate-race fallback). */
async function findContactIdByPhone(phone: string): Promise<string | null> {
    const { data } = await contactsApi.list({ search: phone, limit: 5 });
    return data.find((contact) => contact.phone === phone)?.id ?? null;
}
